import { ecss, cx } from '../reexports/emotion';
import { Theme } from '@mos-cat/ds-theme';
import { BoxHTMLProps } from 'reakit';
import { createComponent, createHook } from 'reakit-system';
import { ResponsiveValue } from 'styled-system';
import { BoxOptions, useBox } from '../Box/Box';
import { StylesOptions, useStyles } from '../hooks/useStyles';
import { useTheme } from '../hooks/useTheme';

import type { BoxOptions as ReakitBoxOptions } from 'reakit/ts';
import type { Component } from 'reakit-system/ts';
import type {
  BackgroundProps,
  BorderProps,
  ColorProps,
  FlexboxProps,
  GridProps,
  LayoutProps,
  PositionProps,
  ShadowProps,
  SpaceProps,
  TLengthStyledSystem,
} from 'styled-system';
import type { Property } from 'csstype';
import { useRequiredPropWarning } from '../hooks/useRequiredPropWarning';

export type StackOptions = BoxOptions & {
  space: ResponsiveValue<keyof Theme['space']>;
} & StylesOptions;

export type StackHTMLProps = BoxHTMLProps;

export type StackProps = StackOptions & StackHTMLProps;

export let useStack = createHook<StackOptions, BoxHTMLProps>({
  name: 'Stack',
  keys: ['space'],
  compose: [useTheme, useBox, useStyles],
  useOptions(options): StackOptions {
    useRequiredPropWarning(Boolean(options.space), 'Stack', 'space');
    return {
      ...options,
      styles: {
        ...(options.styles || {}),
        display: ['flex'],
        flexDirection: ['column'],
        justifyContent: ['flex-start'],
        '& > * + *': {
          mt: options.space as ResponsiveValue<'mt', Theme>,
        },
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(props.className, ecss({ label: 'Stack' })),
    };
  },
});

export let Stack = createComponent({
  useHook: useStack,
  as: 'div',
  memo: true,
});
